/** "Send to OneDrive / Google Drive" — drops a portal file (any /api download route) into
 *  "<Company> Portal/<category>/" on a linked drive. Renders nothing when no drive is linked. */
import { useEffect, useState } from "react";
import { CloudUpload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAMES = { onedrive: "OneDrive", google: "Google Drive" };
let cache = null;   // targets rarely change — share across buttons on a page

/** Linked drives (owner/admin/superadmin) — [] until loaded / when none. */
export function useCloudTargets() {
  const [targets, setTargets] = useState(cache || []);
  useEffect(() => {
    if (cache) return;
    api.get("/cloud/save/targets").then(({ data }) => { cache = data.targets || []; setTargets(cache); }).catch(() => setTargets([]));
  }, []);
  return targets;
}

export async function sendToCloud(provider, { name, apiPath, category = "Files" }) {
  try {
    const { data } = await api.post(`/cloud/${provider}/save`, { name, api_path: apiPath, category });
    toast.success(`Saved to ${NAMES[provider]} → ${data.folder_path}`, { action: data.item?.web_url ? { label: "Open", onClick: () => window.open(data.item.web_url, "_blank") } : undefined });
    return true;
  } catch (e) { toast.error(e?.response?.data?.detail || `Could not send to ${NAMES[provider]}`); return false; }
}
export const cloudName = (p) => NAMES[p] || p;

export default function SendToCloud({ name, apiPath, category = "Files", size = "sm", className = "", label }) {
  const targets = useCloudTargets();
  const [busy, setBusy] = useState(false);
  if (!targets?.length) return null;

  const send = async (provider) => { setBusy(true); await sendToCloud(provider, { name, apiPath, category }); setBusy(false); };
  const cls = size === "icon"
    ? `rounded-md border border-white/15 p-1.5 text-white/70 hover:text-white ${className}`
    : `inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-xs font-semibold text-white/80 hover:bg-white/10 ${className}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={busy} className={cls} title="Send to cloud drive" data-testid="send-to-cloud-btn" onClick={(e) => e.stopPropagation()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}{size !== "icon" && <span>{label || "Send to cloud"}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {targets.map((t) => (
          <DropdownMenuItem key={t.provider} onClick={() => send(t.provider)} data-testid={`send-to-cloud-${t.provider}`}>
            <CloudUpload className="mr-2 h-4 w-4" /> {NAMES[t.provider]} <span className="ml-1 text-xs text-muted-foreground">{t.account}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
